import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { BackButton } from '../../../../shared/components/back-button/back-button';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { StorybookPreviewModal } from '../create-storybooks/storybook-preview-modal/storybook-preview-modal';
import {
  ExportStorybookRequest,
  ExportStorybookResponse,
  StorybookDetail,
  StorybookExportType,
  StorybookPreview,
  StorybookStoryDetail,
} from '../../../../core/models/storybook.models';

/**
 * Read-only "view" of an existing storybook — the Book Preview panel plus
 * Preview/Download actions, reusing the exact same GetStorybookDetail,
 * PreviewStorybook, and ExportStorybook APIs (and the same preview modal)
 * as the create/edit screen, minus the editable form and story picker.
 */
@Component({
  selector: 'app-storybook-view',
  templateUrl: './storybook-view.html',
  styleUrl: './storybook-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, BackButton, TooltipDirective, StorybookPreviewModal],
})
export class StorybookView implements OnInit {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly platformId = inject(PLATFORM_ID);

  readonly storybookId = signal('');
  readonly hasError = signal(false);
  readonly detail = signal<StorybookDetail | null>(null);

  readonly pages = computed(() =>
    [...(this.detail()?.pages ?? [])].sort((a, b) => a.pageOrder - b.pageOrder),
  );

  /** Selected stories, in the same order as storyIdList.sortOrder. */
  readonly stories = computed<StorybookStoryDetail[]>(() => {
    const d = this.detail();
    if (!d) return [];
    const sortOrderByStoryId = new Map(d.storyIdList.map((s) => [s.storyId, s.sortOrder]));
    return [...(d.storyDetails ?? [])].sort(
      (a, b) =>
        (sortOrderByStoryId.get(a.storyId) ?? 0) - (sortOrderByStoryId.get(b.storyId) ?? 0),
    );
  });

  readonly exportingType = signal<StorybookExportType | null>(null);
  readonly StorybookExportType = StorybookExportType;

  readonly isLoadingPreview = signal(false);
  readonly previewData = signal<StorybookPreview | null>(null);

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('storybookId') ?? '';
    this.storybookId.set(id);
    if (!id) {
      this.hasError.set(true);
      return;
    }
    await this.loadDetail(id);
  }

  private async loadDetail(storybookId: string): Promise<void> {
    this.hasError.set(false);
    try {
      const resp = await this.api.request<StorybookDetail>(
        'GET',
        `${API_ROUTES.STORY_BOOK_GET_DETAIL}/${storybookId}`,
        undefined,
        { showToaster: false, showLoader: true, useToken: true },
      );
      if (resp.status && resp.data) {
        this.detail.set(resp.data);
      } else {
        this.hasError.set(true);
      }
    } catch {
      this.hasError.set(true);
    }
  }

  getCoverTitleSizeClass(title: string | null | undefined): string {
    const length = title?.trim().length ?? 0;
    if (length > 60) return 'fm-cover-title-lg';
    if (length > 30) return 'fm-cover-title-md';
    return '';
  }

  /** Formats a duration value in seconds to "M:SS" display string. */
  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  getAuthorInitials(name: string | undefined): string {
    if (!name?.trim()) return '?';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  /** Calls GET /Storybooks/PreviewStorybook/{storybookId} and opens the preview modal. */
  async previewStorybook(): Promise<void> {
    const storybookId = this.storybookId();
    if (!storybookId || this.isLoadingPreview()) return;

    this.isLoadingPreview.set(true);

    try {
      const resp = await this.api.request<StorybookPreview>(
        'GET',
        `${API_ROUTES.STORY_BOOK_PREVIEW}/${storybookId}`,
        undefined,
        { showToaster: false, showLoader: true, useToken: true },
      );

      if (resp.status && resp.data) {
        this.previewData.set(resp.data);
      }
    } finally {
      this.isLoadingPreview.set(false);
    }
  }

  closePreview(): void {
    this.previewData.set(null);
  }

  /** Exports the storybook via POST /Storybooks/ExportStorybook. */
  async exportStorybook(exportType: StorybookExportType): Promise<void> {
    const storybookId = this.storybookId();
    if (!storybookId || this.exportingType() !== null) return;

    this.exportingType.set(exportType);

    const request: ExportStorybookRequest = { storybookId, exportType };

    try {
      const resp = await this.api.request<ExportStorybookResponse>(
        'POST',
        API_ROUTES.STORY_BOOK_EXPORT,
        request,
        { showToaster: true, showLoader: true, useToken: true },
      );

      if (resp.status && resp.data?.fileBytes) {
        const ext = exportType === StorybookExportType.Pdf ? '.pdf' : '.docx';
        const mimeType =
          exportType === StorybookExportType.Pdf
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const fileName = resp.data.fileName.endsWith(ext)
          ? resp.data.fileName
          : `${resp.data.fileName}${ext}`;
        this.downloadBase64File(resp.data.fileBytes, fileName, mimeType);
      }
    } finally {
      this.exportingType.set(null);
    }
  }

  /** Decodes a base64 file payload and triggers a browser download for it. */
  private downloadBase64File(base64: string, fileName: string, mimeType: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }
}
